/**
 * Curated option lists and the flat equality check for the MedChron export appearance/layout
 * field set (`MedChronExportUiState`). Shared by both consumers of `MedChronExportOptionsFields`
 * — the `/profile` Preferences tab and the chronology/cases-list Customize Export dialog — so
 * there is a single source for the offered date-format subset, the Dash/Tab separator samples,
 * the color swatches, and the group/sort choices. The backend accepts any strftime string for
 * `date_format`; this is only the UI's offered subset.
 */
import {
  ChronologySortField,
  ChronologyViewType,
  SortDirection,
} from "@/components/MedicalChronology/Visits/types";
import {
  VISIT_HEADING_SEPARATOR_DASH,
  VISIT_HEADING_SEPARATOR_TAB,
  type VisitHeadingSeparatorToken,
  areMedChronExportUiStatesEqual,
} from "@/services/userPreferencesService.mapper";
import type { VisitHeadingTextColor } from "@/services/userPreferencesService.types";

export { areMedChronExportUiStatesEqual };

const SAMPLE_VISIT_DATE = "7/23/26";
const SAMPLE_VISIT_LOCATION = "Dr. Smith";

export interface DateFormatOption {
  value: string;
  label: string;
  sample: string;
}

export const DATE_FORMAT_OPTIONS: DateFormatOption[] = [
  { value: "%-m/%-d/%y", label: "M/D/YY", sample: "7/23/26" },
  { value: "%-m/%-d/%Y", label: "M/D/YYYY", sample: "7/23/2026" },
  { value: "%B %-d, %Y", label: "Month D, YYYY", sample: "July 23, 2026" },
  { value: "%Y-%m-%d", label: "YYYY-MM-DD", sample: "2026-07-23" },
];

export interface VisitHeadingSeparatorOption {
  token: VisitHeadingSeparatorToken;
  label: string;
  sample: string;
}

export const VISIT_HEADING_SEPARATOR_OPTIONS: VisitHeadingSeparatorOption[] = [
  {
    token: "dash",
    label: "Dash",
    sample: `${SAMPLE_VISIT_DATE}${VISIT_HEADING_SEPARATOR_DASH}${SAMPLE_VISIT_LOCATION}`,
  },
  {
    token: "tab",
    label: "Tab",
    sample: `${SAMPLE_VISIT_DATE}${VISIT_HEADING_SEPARATOR_TAB}${SAMPLE_VISIT_LOCATION}`,
  },
];

export interface VisitHeadingColorOption {
  value: VisitHeadingTextColor;
  label: string;
  swatchClassName: string;
}

export const VISIT_HEADING_COLOR_OPTIONS: VisitHeadingColorOption[] = [
  { value: "blue", label: "Blue", swatchClassName: "bg-blue-600" },
  { value: "black", label: "Black", swatchClassName: "bg-neutral-900" },
];

export interface ChronologyOption {
  value: string;
  label: string;
}

export const GROUP_BY_OPTIONS: ChronologyOption[] = [
  { value: ChronologyViewType.DATE, label: "Chronology" },
  { value: ChronologyViewType.PROVIDER, label: "Provider" },
  { value: ChronologyViewType.VISIT_TYPE, label: "Visit Type" },
  { value: ChronologyViewType.FILE, label: "File" },
];

export const SORT_FIELD_OPTIONS: ChronologyOption[] = [
  { value: ChronologySortField.DATE, label: "Date" },
  { value: ChronologySortField.PAGE, label: "Page" },
];

export const SORT_DIRECTION_OPTIONS: ChronologyOption[] = [
  { value: SortDirection.ASC, label: "Ascending" },
  { value: SortDirection.DESC, label: "Descending" },
];
